import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { TCategoria } from "../../../../../../types/api/comercial/categorias";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DialogProdutos from "../DialogProdutos";
import { TProduto } from "../../../../../../types/api/comercial/produtos";
import ListVinculos from "../ListVinculos";
import { confirm } from "material-ui-confirm";

export default function DialogCriacaoEditCategorias({
  open,
  handleClose,
  categoriaEdit,
  nivel = 0,
  categoriaAdd = false,
}: DialogCriacaoEditCategoriasProps) {
  const queryClient = useQueryClient();
  const MAX_NIVEL = 2;

  const [categoriaState, setCategoriaState] = useState({
    id: undefined as number | undefined,
    nome: "",
    subcategorias: [] as TCategoria[],
    produtos: [] as TProduto[],
  });

  const [dialogState, setDialogState] = useState({
    openSubcategorias: false,
    openProdutos: false,
    openEditCategoria: false,
    categoria: categoriaEdit || null,
  });

  //Querys e mutations
  // Query para buscar os dados da categoria
  const { refetch } = useQuery({
    queryKey: [`datalhar-categoria/${categoriaEdit?.id}`],
    queryFn: () =>
      axiosClient.get(
        `comercial/configuracoes/detalhar-categoria/${categoriaEdit?.id}`
      ),
    retry: false,
    enabled: categoriaEdit?.id !== undefined,
    onSuccess: ({ data }) => {
      setCategoriaState(data);
    },
  });

  const { mutate: criarCategoria, isLoading: isLoadingRegister } = useMutation({
    mutationKey: "criar-categoria",
    mutationFn: (inputs: any) =>
      axiosClient.post(
        "comercial/configuracoes/criar-categoria",
        {
          ...inputs,
          categoria_id: categoriaEdit?.id,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao cadastrar categoria!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-categorias"])
      categoriaAdd && refetch();
      handleCloseClear();
      enqueueSnackbar("Cadastro do banco realizada com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: editarCategoria, isLoading: isLoadingUpdate } = useMutation({
    mutationKey: "editar-categoria",
    mutationFn: (inputs: any) =>
      axiosClient.put(
        `comercial/configuracoes/editar-categoria/${categoriaState.id}`,
        inputs,
        {
          headers: { "Content-Type": "application/json" },
        }
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar categoria!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-categorias"]);
      refetch();
      handleCloseClear();
      enqueueSnackbar("Categoria atualizada com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: deletarCategoria } = useMutation({
    mutationKey: "excluir-categoria",
    mutationFn: (id: number) =>
      axiosClient.delete(`comercial/configuracoes/excluir-categoria/${id}`),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao excluir categoria!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries("pesquisar-categorias");
      refetch();
      enqueueSnackbar("Exclusão de categoria realizada com sucesso!", {
        variant: "success",
      });
    },
  });
  //

  const handleDeleteItem = (id: number, key: "subcategorias" | "produtos") => {
    setCategoriaState((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item.id !== id),
    }));
  };

  const handleSave = () => {
    if (categoriaEdit && !categoriaAdd) {
      editarCategoria({
        id: categoriaState.id,
        nome: categoriaState.nome,
        subcategorias: categoriaState.subcategorias.map((sub) => sub.id),
        produtos: categoriaState.produtos.map((prod) => prod.id),
      });
    } else {
      criarCategoria({ nome: categoriaState.nome });
    }
  };

  const handleCloseClear = () => {
    handleClose();
    setCategoriaState({
      id: undefined,
      nome: "",
      subcategorias: [],
      produtos: [],
    });
  };

  const handleEditItem = async (item: TCategoria) => {
    try {
      const { data } = await axiosClient.get(
        `comercial/configuracoes/detalhar-categoria/${item.id}`
      );
      setDialogState((prev) => ({
        ...prev,
        openEditCategoria: true,
        categoria: data,
      }));
    } catch (error) {
      enqueueSnackbar("Erro ao carregar detalhes da subcategoria!", {
        variant: "error",
      });
    }
  };

  const confirmDelete = async (id: number) => {
    const categoria = await axiosClient.get(
      `comercial/configuracoes/detalhar-categoria/${id}`
    );
    const { subcategorias, produtos } = categoria.data;
    const possuiSubcategorias = subcategorias?.length > 0;
    const possuiProdutos = produtos?.length > 0;

    let mensagem = "Deseja realmente deletá-la?";
    if (possuiSubcategorias && possuiProdutos) {
      mensagem =
        "Essa categoria possui subcategorias e produtos vinculados a ela. Tem certeza que deseja deletá-la?";
    } else if (possuiSubcategorias) {
      mensagem =
        "Essa categoria possui subcategorias vinculadas a ela. Tem certeza que deseja deletá-la?";
    } else if (possuiProdutos) {
      mensagem =
        "Essa categoria possui produtos vinculados a ela. Tem certeza que deseja deletá-la?";
    }

    confirm({ description: mensagem })
      .then(() => deletarCategoria(id))
      .catch(() => {});
  };

  useEffect(() => {
    if (categoriaEdit && !categoriaAdd) {
      setCategoriaState({
        id: categoriaEdit.id,
        nome: categoriaEdit.nome || "",
        subcategorias: categoriaEdit.subcategorias || [],
        produtos: categoriaEdit.produtos || [],
      });
    }
  }, [categoriaEdit, categoriaAdd]);

  return (
    <>
      <Dialog open={open} onClose={handleCloseClear} maxWidth="sm" fullWidth>
        <DialogTitle>
          {categoriaEdit && !categoriaAdd
            ? "Editar Categoria"
            : "Cadastrar Categoria"}
        </DialogTitle>

        <DialogContent>
          <Box px={0.5} my={1}>
            <Grid container spacing={2}>
              <Grid size={{xs:12, sm:12}}>
                <TextField
                  label="Nome"
                  value={categoriaState.nome}
                  onChange={(e) =>
                    setCategoriaState((prev) => ({
                      ...prev,
                      nome: e.target.value,
                    }))
                  }
                  fullWidth
                />
              </Grid>

              {categoriaEdit && !categoriaAdd && (
                <>
                  <ListVinculos
                    title="Subcategoria"
                    items={categoriaState.subcategorias}
                    handleOpenDialog={() =>
                      setDialogState((prev) => ({
                        ...prev,
                        openSubcategorias: true,
                      }))
                    }
                    handleDeleteItem={(id) => confirmDelete(id)}
                    handleEditItem={handleEditItem}
                    emptyListMessage="Nenhuma categoria vinculada."
                    linkButtonText="Vincular categoria"
                    linkButtonIcon={
                      <CategoryOutlinedIcon sx={{ color: "#1b5e20" }} />
                    }
                    disableEdit={!(nivel < MAX_NIVEL)}
                  />

                  <ListVinculos
                    title="Produtos"
                    items={categoriaState.produtos}
                    handleOpenDialog={() =>
                      setDialogState((prev) => ({
                        ...prev,
                        openProdutos: true,
                      }))
                    }
                    handleDeleteItem={(id) => handleDeleteItem(id, "produtos")}
                    handleEditItem={handleEditItem}
                    emptyListMessage="Nenhum produto vinculado."
                    linkButtonText="Vincular produto"
                    linkButtonIcon={
                      <Inventory2OutlinedIcon sx={{ color: "#1b5e20" }} />
                    }
                  />
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleCloseClear}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isLoadingRegister || isLoadingUpdate}
          >
            {(isLoadingRegister || isLoadingUpdate) && (
              <CircularProgress size={20} sx={{ marginRight: 1 }} />
            )}
            {categoriaEdit ? "Atualizar" : "Cadastrar"}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogProdutos
        open={dialogState.openProdutos}
        handleClose={() =>
          setDialogState((prev) => ({ ...prev, openProdutos: false }))
        }
        value={categoriaState.produtos}
        onChange={(newItems) =>
          setCategoriaState((prev) => ({ ...prev, produtos: newItems ?? [] }))
        }
      />

      {dialogState.categoria && nivel < MAX_NIVEL && (
        <DialogCriacaoEditCategorias
          open={dialogState.openEditCategoria}
          handleClose={() => {
            setDialogState((prev) => ({
              ...prev,
              openEditCategoria: false,
              categoria: null,
            }));
          }}
          categoriaEdit={dialogState.categoria}
          nivel={nivel + 1}
        />
      )}

      {dialogState.openSubcategorias && (
        <DialogCriacaoEditCategorias
          open={dialogState.openSubcategorias}
          handleClose={() =>
            setDialogState((prev) => ({ ...prev, openSubcategorias: false }))
          }
          categoriaEdit={categoriaEdit}
          nivel={nivel + 1}
          categoriaAdd
        />
      )}
    </>
  );
}

interface DialogCriacaoEditCategoriasProps {
  open: boolean;
  handleClose: () => void;
  categoriaEdit?: TCategoria | null;
  nivel?: number;
  categoriaAdd?: boolean;
}
